var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { executeAPICall } from '@core/context/executionHelpers';
import { hasValue, IvivaLogoLight } from '@utils';
import { broadcastAuthEvent, DEFAULT_LOGIN_URL } from '@core/auth/authBroadcast';
/**
 * Execute an authentication API call
 * Uses the dedicated /api/auth/* endpoints for v5
 */
function executeAuthAPI(endpoint_1) {
    return __awaiter(this, arguments, void 0, function* (endpoint, params = {}, baseUrl = '') {
        var _a;
        const url = `${baseUrl}${endpoint}`;
        const config = {
            method: 'post',
            url,
            data: params,
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };
        const result = yield executeAPICall(config);
        return Object.assign(Object.assign({}, result), { successful: !result.error && ((_a = result.data) === null || _a === void 0 ? void 0 : _a.Successful) !== false });
    });
}
;
export function loginUser(params_1) {
    return __awaiter(this, arguments, void 0, function* (params, baseUrl = '') {
        return executeAuthAPI('/api/auth/login', {
            LoginID: params.LoginID,
            LoginPassword: params.LoginPassword,
            AuthType: params.AuthType || 'User',
            Next: params.Next || '',
            Remember: params.Remember || ''
        }, baseUrl);
    });
}
export function sendPasswordResetRequest(params_1) {
    return __awaiter(this, arguments, void 0, function* (params, baseUrl = '') {
        return executeAuthAPI('/api/auth/send-reset-request', {
            LoginID: params.LoginID,
            captcha: params.captcha || ''
        }, baseUrl);
    });
}
export function getPasswordResetDetails(hash_1) {
    return __awaiter(this, arguments, void 0, function* (hash, baseUrl = '') {
        const config = {
            method: 'get',
            url: `${baseUrl}/api/auth/reset-details/${hash}`,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        const result = yield executeAPICall(config);
        return Object.assign(Object.assign({}, result), { successful: !result.error });
    });
}
export function resetPassword(params_1) {
    return __awaiter(this, arguments, void 0, function* (params, baseUrl = '') {
        return executeAuthAPI('/api/auth/reset-password', {
            Hash: params.Hash,
            Password: params.Password,
            Password2: params.Password2
        }, baseUrl);
    });
}
export function getSecurityQuestions(hash_1) {
    return __awaiter(this, arguments, void 0, function* (hash, baseUrl = '') {
        const config = {
            method: 'get',
            url: `${baseUrl}/api/auth/security-questions/${hash}`,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        const result = yield executeAPICall(config);
        return Object.assign(Object.assign({}, result), { successful: !result.error });
    });
}
export function validateSecurityQuestion(params_1) {
    return __awaiter(this, arguments, void 0, function* (params, baseUrl = '') {
        var _a, _b, _c, _d;
        // Only include QNum if the corresponding answer exists
        const payload = {
            Hash: params.Hash
        };
        // Add QA1 and QNum1 if answer exists
        if (hasValue(params.QA1)) {
            payload.QA1 = params.QA1;
            payload.QNum1 = (_a = params.QNum1) !== null && _a !== void 0 ? _a : 0;
        }
        // Add QA2 and QNum2 if answer exists
        if (hasValue(params.QA2)) {
            payload.QA2 = params.QA2;
            payload.QNum2 = (_b = params.QNum2) !== null && _b !== void 0 ? _b : 0;
        }
        // Add QA3 and QNum3 if answer exists
        if (hasValue(params.QA3)) {
            payload.QA3 = params.QA3;
            payload.QNum3 = (_c = params.QNum3) !== null && _c !== void 0 ? _c : 0;
        }
        // Add QA4 and QNum4 if answer exists
        if (hasValue(params.QA4)) {
            payload.QA4 = params.QA4;
            payload.QNum4 = (_d = params.QNum4) !== null && _d !== void 0 ? _d : 0;
        }
        return executeAuthAPI('/api/auth/validate-security-questions', payload, baseUrl);
    });
}
// iviva office locations (single source of truth — admin UIs and the login
// globe both read these through getDefaultGlobeLocations)
const DEFAULT_GLOBE_LOCATIONS = [
    { name: 'Singapore', lat: 1.3521, lng: 103.8198 },
    { name: 'Chennai', lat: 13.0827, lng: 80.2707 },
    { name: 'Colombo', lat: 6.9271, lng: 79.8612 },
    { name: 'Dubai', lat: 25.2048, lng: 55.2708 },
    { name: 'Kuala Lumpur', lat: 3.139, lng: 101.6869 },
    { name: 'Bangalore', lat: 12.9716, lng: 77.5946 },
    { name: 'Melbourne', lat: -37.8183, lng: 144.9975 },
];
/**
 * @export
 * Default globe marker locations (iviva offices). Used as the fallback when a
 * globe login background has no configured locations, and by admin UIs to seed
 * the editable list. Returns a fresh copy — safe to mutate.
 *
 * @example
 * ```
 * const offices = getDefaultGlobeLocations();
 * console.log(offices[0]); // { name: "Singapore", lat: 1.3521, lng: 103.8198 }
 * ```
 */
export function getDefaultGlobeLocations() {
    return DEFAULT_GLOBE_LOCATIONS.map((loc) => (Object.assign({}, loc)));
}
// urls: the host's login and logout pages (context.loginUrl / context.logoutUrl).
// With a logoutUrl the host page signs the user out, so /api/auth/logout is not called.
export function logout() {
    return __awaiter(this, arguments, void 0, function* (baseUrl = '', urls = {}) {
        var _a;
        if (urls.logoutUrl) {
            broadcastAuthEvent({ type: 'logout' });
            window.location.replace(urls.logoutUrl);
            return;
        }
        const loginUrl = urls.loginUrl || DEFAULT_LOGIN_URL;
        try {
            const result = yield executeAuthAPI('/api/auth/logout', {}, baseUrl);
            broadcastAuthEvent({ type: 'logout' });
            window.location.replace(((_a = result.data) === null || _a === void 0 ? void 0 : _a.RedirectUrl) || loginUrl);
        }
        catch (_b) {
            window.location.replace(loginUrl);
        }
    });
}
/**
 * Get auth configuration
 * Returns configuration for customizing auth pages appearance
 *
 * @param baseUrl - Base URL for the service (optional)
 * @returns Auth configuration
 */
export function getAuthConfig() {
    return __awaiter(this, arguments, void 0, function* (baseUrl = '') {
        const config = {
            method: 'get',
            url: `${baseUrl}/api/auth/config`,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        const result = yield executeAPICall(config);
        // If no config found, return default configuration
        if (result.error || !result.data) {
            return {
                data: getDefaultAuthConfig(),
                successful: true
            };
        }
        return Object.assign(Object.assign({}, result), { successful: !result.error });
    });
}
/**
 * Get default layout configuration
 * @returns Default AuthLayoutConfig
 */
export function getDefaultLayoutConfig() {
    return {
        background: {
            type: 'color',
            value: 'linear-gradient(135deg, #3A3EFD 0%, #2B3EEA 100%)'
        },
        logo: {
            url: IvivaLogoLight,
            alt: 'Company Logo'
        },
        customStyles: ''
    };
}
/**
 * Get default login form configuration
 * @returns Default LoginFormConfig
 */
export function getDefaultLoginFormConfig() {
    return {
        type: 'island-horizontal',
        formControls: {
            input: {
                bg: '#FFFFFF',
                textColor: '#101828',
                border: '#D0D5DD',
                borderFocus: '#3A3EFD',
                borderRadius: '5rem'
            },
            button: {
                bg: '#181452',
                textColor: '#FFFFFF',
                border: '#181452',
                bgHover: '#2B3EEA',
                textColorHover: '#FFFFFF',
                borderHover: '#2B3EEA',
                bgDisabled: '#F2F4F7',
                textColorDisabled: '#667085',
                borderRadius: '5rem'
            },
            link: {
                color: '#3C2EE2',
                colorHover: '#2B3EEA'
            }
        },
        islandVertical: {
            position: 'bottom-right',
            spacing: { x: '2rem', y: '2rem' },
            maxWidth: '60rem',
            padding: '2rem',
            borderRadius: '1rem',
            border: { width: '1px', color: '#E4E7EC' },
            shadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            bg: '#FFFFFF',
            gap: '2rem',
            glassEffect: false,
            leftPanel: {
                width: '1',
                bg: 'transparent',
                textColor: '#101828',
                padding: '2rem'
            },
            rightPanel: {
                width: '1',
                bg: 'transparent',
                textColor: '#101828',
                padding: '1rem'
            }
        },
        islandHorizontal: {
            position: 'center',
            spacing: { x: '2rem', y: '2rem' },
            maxWidth: '30rem',
            padding: '3rem',
            borderRadius: '1rem',
            border: { width: '1px', color: '#E4E7EC' },
            shadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            bg: '#fff',
            textColor: '#101828',
            glassEffect: false
        },
        fullpage: {
            leftPanel: {
                width: '1',
                bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textColor: '#FFFFFF',
                padding: '4rem'
            },
            rightPanel: {
                width: '1',
                bg: '#FFFFFF',
                textColor: '#101828',
                padding: '2rem'
            }
        }
    };
}
/**
 * Get default auth configuration
 * @returns Default AuthConfig
 */
function getDefaultAuthConfig() {
    return {
        layout: getDefaultLayoutConfig(),
        loginForm: getDefaultLoginFormConfig()
    };
}
/**
 * @deprecated Use getAuthConfig instead
 */
export function getLoginPageConfig() {
    return __awaiter(this, arguments, void 0, function* (baseUrl = '') {
        return getAuthConfig(baseUrl);
    });
}
/**
 * Save auth configuration
 * Saves configuration for customizing auth pages appearance
 *
 * @param config - Auth configuration to save
 * @param baseUrl - Base URL for the service (optional)
 * @returns Result of save operation
 */
export function saveAuthConfig(config_1) {
    return __awaiter(this, arguments, void 0, function* (config, baseUrl = '') {
        const axiosConfig = {
            method: 'post',
            url: `${baseUrl}/api/auth/config`,
            data: config,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        const result = yield executeAPICall(axiosConfig);
        return Object.assign(Object.assign({}, result), { successful: !result.error });
    });
}
